import React, { useEffect, useRef, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";

const DRAG_HANDLE_CLASS = "canvas-drag-handle";

type Props = {
  label: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onDuplicate: () => void;
  onDelete: () => void;
  onMoveLayer: (direction: "up" | "down") => void;
  onFitWidth: () => void;
  onBringToFront: () => void;
  onSendToBack: () => void;
};

export default function ElementSelectionToolbar({
  label,
  canMoveUp,
  canMoveDown,
  onDuplicate,
  onDelete,
  onMoveLayer,
  onFitWidth,
  onBringToFront,
  onSendToBack,
}: Props) {
  const [menu, setMenu] = useState<"layer" | "more" | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menu) return;
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setMenu(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menu]);

  const stop = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const onToolbarMouseDown = (event: React.MouseEvent) => {
    // Let react-rnd receive mousedown from the drag handle — do not stopPropagation there.
    const target = event.target as HTMLElement | null;
    if (target?.closest?.(`.${DRAG_HANDLE_CLASS}`)) return;
    stop(event);
  };

  return (
    <div
      ref={rootRef}
      className="canvas-element-toolbar"
      onMouseDown={onToolbarMouseDown}
      onClick={stop}
      onDoubleClick={stop}
    >
      <div
        className={`${DRAG_HANDLE_CLASS} canvas-element-toolbar__drag-group`}
        title="Drag to move"
      >
        <span className="canvas-element-toolbar__drag">
          <OpenWithIcon sx={{ fontSize: 16 }} />
        </span>
        <span className="canvas-element-toolbar__type">{label}</span>
      </div>
      <span className="canvas-element-toolbar__divider" aria-hidden />

      <Tooltip title="Duplicate" arrow>
        <button
          type="button"
          className="canvas-element-toolbar__btn"
          aria-label="Duplicate"
          onClick={(e) => {
            stop(e);
            onDuplicate();
          }}
        >
          <ContentCopyOutlinedIcon sx={{ fontSize: 17 }} />
        </button>
      </Tooltip>

      <div style={{ position: "relative" }}>
        <Tooltip title="Layer" arrow>
          <button
            type="button"
            className="canvas-element-toolbar__btn"
            aria-label="Layer"
            aria-expanded={menu === "layer"}
            onClick={(e) => {
              stop(e);
              setMenu((m) => (m === "layer" ? null : "layer"));
            }}
          >
            <LayersOutlinedIcon sx={{ fontSize: 17 }} />
          </button>
        </Tooltip>
        {menu === "layer" ? (
          <div className="canvas-element-toolbar__menu" role="menu">
            <button
              type="button"
              className="canvas-element-toolbar__menu-item"
              disabled={!canMoveUp}
              onClick={(e) => {
                stop(e);
                onMoveLayer("up");
                setMenu(null);
              }}
            >
              Bring forward
            </button>
            <button
              type="button"
              className="canvas-element-toolbar__menu-item"
              disabled={!canMoveDown}
              onClick={(e) => {
                stop(e);
                onMoveLayer("down");
                setMenu(null);
              }}
            >
              Send backward
            </button>
            <button
              type="button"
              className="canvas-element-toolbar__menu-item"
              disabled={!canMoveUp}
              onClick={(e) => {
                stop(e);
                onBringToFront();
                setMenu(null);
              }}
            >
              Bring to front
            </button>
            <button
              type="button"
              className="canvas-element-toolbar__menu-item"
              disabled={!canMoveDown}
              onClick={(e) => {
                stop(e);
                onSendToBack();
                setMenu(null);
              }}
            >
              Send to back
            </button>
          </div>
        ) : null}
      </div>

      <Tooltip title="Fit to slide width" arrow>
        <button
          type="button"
          className="canvas-element-toolbar__btn"
          aria-label="Fit to slide width"
          onClick={(e) => {
            stop(e);
            onFitWidth();
          }}
        >
          <SyncAltOutlinedIcon sx={{ fontSize: 17 }} />
        </button>
      </Tooltip>

      <Tooltip title="Delete" arrow>
        <button
          type="button"
          className="canvas-element-toolbar__btn canvas-element-toolbar__btn--danger"
          aria-label="Delete"
          onClick={(e) => {
            stop(e);
            onDelete();
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 17 }} />
        </button>
      </Tooltip>

      <div style={{ position: "relative" }}>
        <Tooltip title="More" arrow>
          <button
            type="button"
            className="canvas-element-toolbar__btn"
            aria-label="More"
            aria-expanded={menu === "more"}
            onClick={(e) => {
              stop(e);
              setMenu((m) => (m === "more" ? null : "more"));
            }}
          >
            <MoreVertIcon sx={{ fontSize: 17 }} />
          </button>
        </Tooltip>
        {menu === "more" ? (
          <div className="canvas-element-toolbar__menu" role="menu">
            <button
              type="button"
              className="canvas-element-toolbar__menu-item"
              onClick={(e) => {
                stop(e);
                onDuplicate();
                setMenu(null);
              }}
            >
              Duplicate
            </button>
            <button
              type="button"
              className="canvas-element-toolbar__menu-item"
              onClick={(e) => {
                stop(e);
                onFitWidth();
                setMenu(null);
              }}
            >
              Fit to slide width
            </button>
            <button
              type="button"
              className="canvas-element-toolbar__menu-item"
              style={{ color: "#b91c1c" }}
              onClick={(e) => {
                stop(e);
                onDelete();
                setMenu(null);
              }}
            >
              Delete
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export { DRAG_HANDLE_CLASS };
